import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import type { FileItem } from "../types";
import type { DisplayAttribute } from "@/features/admin/panels/categories/hooks/useCategoryAttributesDisplay";

interface UseEditFileAttributesFormOptions {
  file: FileItem;
  categoryAttributes: DisplayAttribute[];
  open: boolean;
}

export function useEditFileAttributesForm({
  file,
  categoryAttributes,
  open,
}: UseEditFileAttributesFormOptions) {
  const [attributeValues, setAttributeValues] = useState<
    Record<string | number, string | number | undefined>
  >({});

  useEffect(() => {
    if (open && file.attributeValues && categoryAttributes.length > 0) {
      const initialValues: Record<
        string | number,
        string | number | undefined
      > = {};

      file.attributeValues.forEach((attrValue) => {
        const attribute = categoryAttributes.find(
          (attr) => attr.name === attrValue.attributeName
        );
        if (attribute) {
          initialValues[attribute.id] = attrValue.value;
        }
      });

      setAttributeValues(initialValues);
    }
  }, [open, file.attributeValues, categoryAttributes]);

  useEffect(() => {
    if (!open) {
      setAttributeValues({});
    }
  }, [open]);

  const handleAttributeChange = useCallback(
    (attributeId: string | number, value: string | number | undefined) => {
      setAttributeValues((prev) => ({
        ...prev,
        [attributeId]: value,
      }));
    },
    []
  );

  const requiredAttributes = useMemo(
    () => categoryAttributes.filter((attr) => attr.isRequired),
    [categoryAttributes]
  );

  const optionalAttributes = useMemo(
    () => categoryAttributes.filter((attr) => !attr.isRequired),
    [categoryAttributes]
  );

  const validateAndPrepareData = useCallback(() => {
    const attributeValuesArray = Object.entries(attributeValues)
      .filter(([_, value]) => value !== undefined && value !== "")
      .map(([attributeId, value]) => ({
        attributeId: Number(attributeId),
        value: String(value),
      }));

    if (attributeValuesArray.length === 0) {
      toast.error("No attributes selected", {
        description: "Please select at least one attribute value to update.",
      });
      return null;
    }

    const missingRequired = requiredAttributes.filter(
      (attr) => !attributeValuesArray.some((av) => av.attributeId === attr.id)
    );

    if (missingRequired.length > 0) {
      toast.error("Required attributes missing", {
        description: `Please fill in all required attributes: ${missingRequired.map((attr) => attr.name).join(", ")}`,
      });
      return null;
    }

    return attributeValuesArray;
  }, [attributeValues, requiredAttributes]);

  return {
    attributeValues,
    handleAttributeChange,
    requiredAttributes,
    optionalAttributes,
    validateAndPrepareData,
  };
}
